import {
    Component,
    Emitter,
    Material,
    Mesh,
    MeshComponent,
    Object3D,
} from '@wonderlandengine/api';
import {property} from '@wonderlandengine/api/decorators.js';
import {quat, vec3} from 'gl-matrix';
import {RNG} from '../utils/rng.js';
import {Easing, lerpVec3} from '../utils/lerp.js';

const tempVec = vec3.create();
const tempCameraVec = vec3.create();
const tempVec2 = vec3.create();
const tempQuat = quat.create();

/* Adapted from https://gitlab.com/wonderland-gmbh/webxr-developers/fruit-ninja/-/blob/0e7f17d6cf0d2f5dd9494fc830e42dabd268a2c6/js/burst-mesh-particles.js */
export class BurstMeshParticles extends Component {
    static TypeName = 'burst-mesh-particles';

    /* Mesh for spawned particles */
    @property.mesh()
    mesh!: Mesh | null;
    /* Material for spawned particles */
    @property.material()
    material!: Material | null;

    @property.float(9.81)
    gravity = 9.81;

    /* Duration of effect  */
    @property.float(5.0)
    duration!: number;
    /* Number of particles */
    @property.int(20)
    numParticles!: number;
    /* Initial speed of emitted particles */
    @property.float(5.0)
    initialSpeed!: number;
    /* Size of a particle */
    @property.float(0.1)
    particleScale!: number;

    @property.bool(false)
    billboard!: boolean;

    @property.bool(false)
    randomRotation = false;

    @property.float(0.1)
    lifetimeLow = 0.1;
    @property.float(0.1)
    lifetimeHigh = 0.3;

    @property.float(1.0)
    startScaleX = 1.0;
    @property.float(1.0)
    startScaleY = 1.0;
    @property.float(1.0)
    startScaleZ = 4.0;

    @property.float(1.0)
    endScaleX = 1.0;
    @property.float(1.0)
    endScaleY = 1.0;
    @property.float(1.0)
    endScaleZ = 4.0;

    onFinished = new Emitter();

    #time = -1.0;

    _objects: Object3D[] = [];
    _velocities: [number, number, number][] = [];
    _lifeTimes: number[] = [];

    startScale = vec3.create();
    endScale = vec3.create();

    start() {
        this._objects = this.scene.addObjects(this.numParticles, null, this.numParticles);
        this._velocities = [];
        this._lifeTimes = [];

        this.startScale = vec3.fromValues(
            this.startScaleX,
            this.startScaleY,
            this.startScaleZ
        );
        this.endScale = vec3.fromValues(this.endScaleX, this.endScaleY, this.endScaleZ);

        for (let i = 0; i < this.numParticles; ++i) {
            this._velocities.push([0, 0, 0]);
            const obj = this._objects[i];
            obj.name = 'particle' + i.toString();
            const mesh = obj.addComponent('mesh');
            mesh.mesh = this.mesh;
            mesh.material = this.material;
            /* Most efficient way to hide the mesh */
            obj.setScalingLocal([0, 0, 0]);
        }

        // setInterval(() => {
        //     this.spawn(this.object.getPositionWorld([]));
        // }, 2000);
    }

    setMaterial(i: number, mat: Material | null) {
        this._objects[i].getComponent('mesh')!.material = mat;
    }

    update(dt: number) {
        if (this.#time < 0.0) return;

        this.#time += dt;

        if (this.#time > this.duration) {
            for (let i = 0; i < this.numParticles; ++i) {
                const obj = this._objects[i];
                obj.setScalingLocal([0, 0, 0]);
            }
            this.onFinished.notify();
            this.#time = -1.0;
            return;
        }

        const distance: vec3 = [0, 0, 0];

        if (this.billboard) {
            this.engine.scene.activeViews[0].object.getPositionWorld(tempCameraVec);
            quat.fromEuler(tempQuat, 0, 180, 0);
        }

        for (let i = 0; i < this.numParticles; ++i) {
            if (this.#time > this._lifeTimes[i]) {
                this._objects[i].setScalingLocal([0, 0, 0]);
                continue;
            }

            // change scale over lifetime
            const t = this.#time / this._lifeTimes[i];

            lerpVec3(tempVec2, this.startScale, this.endScale, t, Easing.InQuad);
            this._objects[i].setScalingLocal(tempVec2);

            /* Apply gravity */
            if (this.gravity !== 0.0) {
                const vel = this._velocities[i];
                vel[1] -= this.gravity * dt;
            }

            /* Apply velocity */
            vec3.scale(distance, this._velocities[i], dt);
            this._objects[i].translateLocal(distance);
            if (this.billboard) {
                this._objects[i].lookAt(tempCameraVec);

                this._objects[i].rotateLocal(tempQuat);
            }
        }
    }

    setColor(color: number[]) {
        this.material!.color = color;
    }

    /* Spawn the particles */
    spawn(position: ArrayLike<number>) {
        if (!this.active) return;
        this.#time = 0.0;

        for (let i = 0; i < this.numParticles; ++i) {
            const obj = this._objects[i];
            obj.resetTransform();
            obj.setScalingLocal([
                this.particleScale * this.startScaleX,
                this.particleScale * this.startScaleY,
                this.particleScale * this.startScaleZ,
            ]);

            if (this.randomRotation) {
                quat.fromEuler(
                    tempQuat,
                    Math.random() * 360,
                    Math.random() * 360,
                    Math.random() * 360
                );
                obj.setRotationWorld(tempQuat);
            }

            obj.setPositionWorld(position);

            this._velocities[i][0] = Math.random() - 0.5;
            this._velocities[i][1] = Math.random();
            this._velocities[i][2] = Math.random() - 0.5;

            this._lifeTimes[i] = RNG.getUniform(this.lifetimeLow, this.lifetimeHigh);
            vec3.normalize(this._velocities[i], this._velocities[i]);
            vec3.scale(this._velocities[i], this._velocities[i], this.initialSpeed);
        }
    }
}
